import React, { useState, useEffect } from "react";
import { Paper, Typography, Grid2 } from "@mui/material";
import { useForm } from 'react-hook-form';
import axios from "axios";
import { useMutation } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from 'zod';

import BreadCrumbs from "@components/breadCrumbs/breadCrumbs";
import CustomButton from "@components/button/customButton";
import ConfirmDialog from "@components/dialog/confirmDialog";
import SkeletonWrapper from "@components/skeleton/skeletonWrapper";
import ControlledTextField from "@components/inputs/controlledTextField";

import { useSnackbar } from "@context/snackbarContext";

import { useAuthentication } from "@redux/authentication/hook";

import { AdminApiEndpoints } from "@configuration/apiEndpoints";


const schema = z.object({
  headerCntrad: z.string().min(1, { message: '標頭 is required' }),
  contentCntrad: z.string().min(1, { message: '內容 is required' }),

  headerCnsimp: z.string().min(1, { message: '标头 is required' }),
  contentCnsimp: z.string().min(1, { message: '內容 is required' }),

  headerEng: z.string().min(1, { message: 'Header is required' }),
  contentEng: z.string().min(1, { message: 'Content is required' }),
});

export default function Section1Form() {
  const openSnackbar = useSnackbar();
  const { user } = useAuthentication()

  const { handleSubmit, control, getValues, reset } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
    },
  });

  const [openConfirmDialog, setOpenConfirmDialog] = useState(false)

  useEffect(()=>{
    fetchSectionMutation.mutate()
  },[])

  const fetchSectionMutation = useMutation({
    mutationFn: () => {
      return axios.get(`${AdminApiEndpoints.getAboutUsSection1}`)
    },
    onSuccess: (res) => {
      const data = res.data
      reset(data)
    },
    onError: (err) => {
      openSnackbar("Failed to retrieve section", "error");
    }
  });

  /*const createSectionMutation = useMutation({
    mutationFn: (data) => {
      return axios.post(`${AdminApiEndpoints.createAboutUsSection1}`, {...data, by: user.username})
    },
    onSuccess: () => {
      setOpenConfirmDialog(false)
      openSnackbar("Successfully created section", "success");
      fetchSectionMutation.mutate()
    },
    onError: (err) => {
      setOpenConfirmDialog(false)
      if (err?.response) openSnackbar(err?.response?.data, "error");
      else openSnackbar("Failed to create section", "error");
    }
  })*/

  const updateSectionMutation = useMutation({
    mutationFn: (data) => {
      return axios.post(`${AdminApiEndpoints.updateAboutUsSection1}`, {...data, by: user.username})
    },
    onSuccess: () => {
      setOpenConfirmDialog(false)
      openSnackbar("Successfully updated section", "success");
      fetchSectionMutation.mutate()
    },
    onError: (err) => {
      setOpenConfirmDialog(false)
      if (err?.response) openSnackbar(err?.response?.data, "error");
      else openSnackbar("Failed to update section", "error");
    }
  })

  const onSubmit = () => {
    setOpenConfirmDialog(true)
  };

  const onConfirmSubmit = () => {
    const data = getValues();
    updateSectionMutation.mutate(data)
  }

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: "25px"}}>
      <BreadCrumbs />
      <SkeletonWrapper isLoading={fetchSectionMutation.isPending} isError={fetchSectionMutation.isError} errorMessage="Unable to load section">
      <form onSubmit={handleSubmit(onSubmit)} style={{display: 'flex', flexDirection: 'column', gap: "25px"}}>
        <Grid2 container spacing={"25px"}>
          <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
            <Paper
              sx={{
                p: "20px 25px",
                boxShadow: "none",
                borderRadius: "10px",
                border: "1px solid #E5E5E5",
              }}
            >
              <Grid2 container spacing={"20px"}>
                <Grid2 size={12}>
                  <Typography fontSize={"0.9rem"} color="#202020" fontWeight={600}>繁体中文</Typography>
                </Grid2>
                <Grid2 size={12}>
                  <ControlledTextField 
                    name="headerCntrad"
                    control={control}
                    label="標頭"
                    disabled={fetchSectionMutation.isPending || fetchSectionMutation.isError}
                  />
                </Grid2>

                <Grid2 size={12}>
                  <ControlledTextField 
                    name="contentCntrad"
                    control={control}
                    label="內容"
                    disabled={fetchSectionMutation.isPending || fetchSectionMutation.isError}
                    multiline
                    rows={8}
                  />
                </Grid2>

              </Grid2>
            </Paper>
          </Grid2>

          <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
            <Paper
              sx={{
                p: "20px 25px",
                boxShadow: "none",
                borderRadius: "10px",
                border: "1px solid #E5E5E5",
              }}
            >
              <Grid2 container spacing={"20px"}>
                <Grid2 size={12}>
                  <Typography fontSize={"0.9rem"} color="#202020" fontWeight={600}>简体中文</Typography>
                </Grid2>
                <Grid2 size={12}>
                  <ControlledTextField 
                    name="headerCnsimp"
                    control={control}
                    label="标头"
                    disabled={fetchSectionMutation.isPending || fetchSectionMutation.isError}
                  />
                </Grid2>

                <Grid2 size={12}>
                  <ControlledTextField 
                    name="contentCnsimp"
                    control={control}
                    label="内容"
                    disabled={fetchSectionMutation.isPending || fetchSectionMutation.isError}
                    multiline
                    rows={8}
                  />
                </Grid2>

              </Grid2>
            </Paper>
          </Grid2>

          <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
            <Paper
              sx={{
                p: "20px 25px",
                boxShadow: "none",
                borderRadius: "10px",
                border: "1px solid #E5E5E5",
              }}
            >
              <Grid2 container spacing={"20px"}>
                <Grid2 size={12}>
                  <Typography fontSize={"0.9rem"} color="#202020" fontWeight={600}>English</Typography>
                </Grid2>
                <Grid2 size={12}>
                  <ControlledTextField 
                    name="headerEng"
                    control={control}
                    label="Header"
                    disabled={fetchSectionMutation.isPending || fetchSectionMutation.isError}
                  />
                </Grid2>

                <Grid2 size={12}>
                  <ControlledTextField 
                    name="contentEng"
                    control={control}
                    label="Content"
                    disabled={fetchSectionMutation.isPending || fetchSectionMutation.isError}
                    multiline
                    rows={8}
                  />
                </Grid2>

              </Grid2>
            </Paper>
          </Grid2>

        </Grid2>

        <Paper
          sx={{
            p: "20px 25px",
            boxShadow: "none",
            borderRadius: "10px",
            border: "1px solid #E5E5E5",
            display: "flex",
            justifyContent: 'flex-end',
            gap: "10px"
          }}
        >
          <CustomButton type="submit" disabled={fetchSectionMutation.isPending || fetchSectionMutation.isError}>Save</CustomButton>
        </Paper>
        
      </form>
      </SkeletonWrapper>
      <ConfirmDialog
        open={openConfirmDialog}
        onClose={()=>setOpenConfirmDialog(false)}
        dialogTitle="Confirmation"
        dialogDescription="Update section?"
        onSubmit={()=>onConfirmSubmit()}
        loading={updateSectionMutation.isPending}
      />
    </div>
  );
}
